import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { apiErrorText } from "@/lib/apiError";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import CsvUploader from "@/components/app/CsvUploader";
import RegistryFieldEditor from "@/components/uni-portal/RegistryFieldEditor";
import BulkVerifyWizard from "@/components/uni-portal/BulkVerifyWizard";
import EmptyCellHelpLink from "@/components/app/EmptyCellHelpLink";
import OwnerFlagsInbox from "@/components/uni-portal/OwnerFlagsInbox";
import DigestPrefToggle from "@/components/uni-portal/DigestPrefToggle";
import {
  ArrowLeft,
  BookOpen,
  Loader2,
  Lock,
  Save,
  ShieldCheck,
  ShieldAlert,
  Sparkles,
  Wand2,
} from "lucide-react";

/**
 * /uni-portal/uni/:id — university-level edit form.
 * Below the form, lists the uni's degrees with links to the degree edit
 * page. Non-owners get a 403 view.
 *
 * Feb 2026 policy: universities may only edit factual, verifiable
 * fields via the portal. Marketing copy (tagline, description) is
 * staff-only and displayed here read-only with a "request an edit"
 * affordance that routes to the WUA data-gap report queue.
 */
const UNI_FIELDS = [
  { key: "tuition_intl_annual_usd", label: "Tuition — international (USD/yr)", type: "number" },
  { key: "tuition_domestic_annual_usd", label: "Tuition — domestic (USD/yr)", type: "number" },
  { key: "accommodation_annual_usd", label: "Accommodation (USD/yr)", type: "number" },
  { key: "food_annual_usd", label: "Food (USD/yr)", type: "number" },
  { key: "acceptance_rate", label: "Overall acceptance rate (0–1)", type: "number", step: 0.01, max: 1 },
];

const STAFF_ONLY_UNI_FIELDS = [
  { key: "tagline",     label: "Tagline",     section: "identity", field: "Tagline" },
  { key: "description", label: "Description", section: "identity", field: "Description" },
];

export default function UniPortalEdit() {
  const { id } = useParams();
  const { user, loading: authLoading, login } = useAuth();
  const [state, setState] = useState({ loading: true, uni: null, degrees: [], forbidden: false });
  const [recentEdits, setRecentEdits] = useState([]);
  const [form, setForm] = useState({});
  const [degreeFilter, setDegreeFilter] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);

  useEffect(() => {
    if (authLoading || !user) {
      setState((s) => ({ ...s, loading: authLoading }));
      return;
    }
    (async () => {
      try {
        const [u, d, r] = await Promise.all([
          api.get(`/universities/${id}`),
          api.get(`/uni-portal/degrees/${id}`),
          api.get(`/uni-portal/university/${id}/recent-edits?limit=5`)
            .catch(() => ({ data: { items: [] } })),
        ]);
        setState({ loading: false, uni: u.data, degrees: d.data?.items || [], forbidden: false });
        setRecentEdits(r.data?.items || []);
        // Prefill the form from the current uni state
        const init = {};
        for (const f of UNI_FIELDS) init[f.key] = u.data?.[f.key] ?? "";
        setForm(init);
      } catch (err) {
        const st = err?.response?.status;
        setState({ loading: false, uni: null, degrees: [], forbidden: st === 401 || st === 403 });
      }
    })();
  }, [id, user, authLoading]);

  const change = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    const patch = { correction_note: note || undefined };
    for (const f of UNI_FIELDS) {
      const v = form[f.key];
      if (v === "" || v === null || v === undefined) continue;
      if (f.type === "number") {
        const num = Number(v);
        if (!Number.isNaN(num)) patch[f.key] = num;
      } else {
        patch[f.key] = String(v);
      }
    }
    try {
      const { data } = await api.patch(`/uni-portal/university/${id}`, patch);
      toast.success(`Saved — ${data.patched_fields.length} field(s) updated`);
      setNote("");
    } catch (err) {
      toast.error(apiErrorText(err, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  if (authLoading || state.loading) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-24 text-stone-500 font-mono text-sm" data-testid="uni-portal-edit-loading">
        <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…
      </div>
    );
  }

  if (!user) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-16" data-testid="uni-portal-edit-signin">
        <div className="p-8 border border-stone-300 rounded-lg bg-white">
          <ShieldAlert className="h-8 w-8 text-stone-500" />
          <h1 className="mt-3 font-editorial text-2xl">Sign in required</h1>
          <Button className="mt-4" onClick={login}>Sign in</Button>
        </div>
      </div>
    );
  }

  if (state.forbidden || !state.uni) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-16" data-testid="uni-portal-edit-forbidden">
        <div className="p-8 border border-stone-300 rounded-lg bg-white">
          <ShieldAlert className="h-8 w-8 text-stone-500" />
          <h1 className="mt-3 font-editorial text-2xl">You don't own this university</h1>
          <p className="mt-2 text-stone-600 text-sm">
            Only verified owners can edit fields. Submit a claim to request access.
          </p>
          <Link to={`/uni/${id}/claim`}>
            <Button className="mt-4">Claim this university</Button>
          </Link>
        </div>
      </div>
    );
  }

  const { uni, degrees } = state;

  return (
    <div className="max-w-5xl mx-auto px-6 py-10" data-testid="uni-portal-edit-page">
      <Link to="/uni-portal" className="inline-flex items-center gap-1 text-[11px] font-underground-sc tracking-wider text-stone-500 hover:text-stone-900">
        <ArrowLeft className="h-3 w-3" /> Portal
      </Link>
      <div className="mt-3 flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 text-emerald-700">
            <ShieldCheck className="h-4 w-4" />
            <span className="text-[10px] font-underground-sc tracking-widest">
              Verified owner
            </span>
          </div>
          <h1 className="mt-1 font-editorial text-4xl">{uni.name}</h1>
          <p className="mt-1 text-stone-500 text-sm">
            {uni.city}, {uni.country}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to={`/uni/${id}/trust`}>
            <Button size="sm" variant="ghost" data-testid="uni-portal-trust-link">
              Public receipt
            </Button>
          </Link>
          <Link to={`/uni/${id}`}>
            <Button size="sm" variant="outline">View public page</Button>
          </Link>
        </div>
      </div>

      <div className="mt-6">
        <DigestPrefToggle
          universityId={id}
          initialValue={uni.digest_pref}
        />
      </div>

      <div
        className="mt-6 mb-4 p-4 border border-emerald-200 rounded-xl bg-emerald-50/60 flex items-center justify-between gap-3 flex-wrap"
        data-testid="uni-portal-uni-wizard-cta"
      >
        <div className="flex items-center gap-2 min-w-0">
          <Wand2 className="h-4 w-4 text-emerald-800 shrink-0" />
          <div className="min-w-0">
            <div className="text-[13px] font-medium text-stone-900">
              Verify multiple factual fields at once
            </div>
            <div className="text-[11px] text-stone-500 leading-snug">
              Step through every empty university-level field one at a time.
              Skip anything you can't answer — every save updates the
              audit trail immediately.
            </div>
          </div>
        </div>
        <Button
          onClick={() => setWizardOpen(true)}
          data-testid="uni-portal-uni-wizard-open"
          className="bg-emerald-700 hover:bg-emerald-800 text-white"
        >
          <Wand2 className="h-4 w-4 mr-1" />
          Start bulk verify
        </Button>
      </div>

      <BulkVerifyWizard
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        docType="universities"
        docId={id}
        doc={uni}
      />

      <RegistryFieldEditor docType="universities" docId={id} doc={uni} />

      <OwnerFlagsInbox universityId={id} />

      <div
        className="mt-8 p-6 border border-stone-300 rounded-xl bg-stone-50"
        data-testid="uni-portal-staff-only-identity"
      >
        <div className="flex items-center gap-2">
          <Lock className="h-4 w-4 text-stone-500" />
          <h2 className="font-editorial text-xl">Identity — staff-managed</h2>
        </div>
        <p className="mt-1 text-xs text-stone-500">
          These fields are marketing / narrative copy and can only be
          edited by WUA editorial staff (Feb 2026 policy). Spot something
          wrong? Send us the correction and we'll action it in the audit
          trail.
        </p>
        <div className="mt-4 space-y-4">
          {STAFF_ONLY_UNI_FIELDS.map((f) => {
            const current = uni?.[f.key];
            return (
              <div
                key={f.key}
                data-testid={`uni-portal-staff-only-${f.key}`}
              >
                <div className="flex items-baseline justify-between gap-3">
                  <label className="text-[11px] font-underground-sc tracking-wider text-stone-600">
                    {f.label}
                  </label>
                  <EmptyCellHelpLink
                    universityId={id}
                    section={f.section}
                    field={f.field}
                    label="request an edit"
                  />
                </div>
                <div
                  className="mt-1 p-3 border border-stone-200 rounded-md bg-white text-sm text-stone-700 whitespace-pre-wrap"
                  data-testid={`uni-portal-staff-only-value-${f.key}`}
                >
                  {current ? String(current) : (
                    <span className="italic text-stone-400">— not set —</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-8 p-6 border border-stone-300 rounded-xl bg-white">
        <h2 className="font-editorial text-xl">University-level fields</h2>
        <p className="mt-1 text-xs text-stone-500">
          Only fill the fields you want to change. Blank fields are left
          untouched.
        </p>
        <div className="mt-5 space-y-4">
          {UNI_FIELDS.map((f) => (
            <div key={f.key}>
              <label className="text-[11px] font-underground-sc tracking-wider text-stone-600">
                {f.label}
              </label>
              {f.type === "textarea" ? (
                <Textarea
                  className="mt-1"
                  rows={f.rows || 4}
                  value={form[f.key] ?? ""}
                  onChange={(e) => change(f.key, e.target.value)}
                  data-testid={`uni-portal-input-${f.key}`}
                />
              ) : (
                <Input
                  className="mt-1"
                  type={f.type}
                  step={f.step}
                  max={f.max}
                  value={form[f.key] ?? ""}
                  onChange={(e) => change(f.key, e.target.value)}
                  data-testid={`uni-portal-input-${f.key}`}
                />
              )}
            </div>
          ))}
          <div>
            <label className="text-[11px] font-underground-sc tracking-wider text-stone-600">
              Correction note (private — for editors)
            </label>
            <Textarea
              className="mt-1"
              rows={2}
              placeholder="e.g. Updated 2027 intake tuition — source: press release 24 Feb 2027"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid="uni-portal-note-input"
              maxLength={1000}
            />
          </div>
          <div className="pt-2">
            <Button onClick={save} disabled={saving} data-testid="uni-portal-save-btn">
              <Save className="h-4 w-4 mr-2" />
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </div>
      </div>

      <div className="mt-10">
        {recentEdits.length > 0 && (
          <div
            className="mb-6 p-4 border border-emerald-200 rounded-xl bg-emerald-50/60"
            data-testid="uni-portal-recent-edits-strip"
          >
            <div className="flex items-center gap-2 text-emerald-800">
              <Sparkles className="h-4 w-4" />
              <span className="text-[10px] font-underground-sc tracking-widest">
                Recently verified
              </span>
            </div>
            <ul className="mt-2 space-y-1.5 text-[13px] text-stone-800">
              {recentEdits.map((r, i) => (
                <li
                  key={r.id || i}
                  className="flex items-start gap-2 leading-snug"
                  data-testid={`uni-portal-recent-edit-${i}`}
                >
                  <span className="text-emerald-700 shrink-0 font-mono text-[11px] mt-0.5">
                    ✓
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="font-medium">{r.field_label}</span>
                    {r.doc_title ? (
                      <>
                        <span className="text-stone-400"> · </span>
                        <span className="text-stone-600 truncate">{r.doc_title}</span>
                      </>
                    ) : (
                      <>
                        <span className="text-stone-400"> · </span>
                        <span className="text-stone-600">University</span>
                      </>
                    )}
                    <span className="text-stone-400 text-[11px] font-mono ml-2">
                      {r.actor_type === "university" ? "by uni" : "by WUA"}
                      {r.created_at ? ` · ${new Date(r.created_at).toLocaleDateString()}` : ""}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
        <h2 className="font-editorial text-xl flex items-center gap-2">
          <BookOpen className="h-5 w-5" /> Degrees ({degrees.length})
        </h2>
        <p className="mt-1 text-xs text-stone-500">
          Edit any degree's tuition, offer rate, English tests and entry
          requirements. Every change is stamped with "Verified by
          university · today's date".
        </p>

        <div className="mt-4">
          <CsvUploader
            scope="university"
            id={id}
            onCommitted={async () => {
              try {
                const { data } = await api.get(`/uni-portal/degrees/${id}`);
                setState((s) => ({ ...s, degrees: data?.items || [] }));
              } catch (err) {
                toast.error(apiErrorText(err, "Failed to refresh degree list"));
              }
            }}
          />
        </div>

        <Input
          value={degreeFilter}
          onChange={(e) => setDegreeFilter(e.target.value)}
          placeholder="Filter — course title, id, qualification, campus…"
          className="mt-4"
          data-testid="uni-portal-degree-filter"
        />

        <ul className="mt-4 divide-y divide-stone-200 border border-stone-200 rounded-lg bg-white">
          {degrees
            .filter((d) => {
              if (!degreeFilter.trim()) return true;
              const q = degreeFilter.trim().toLowerCase();
              return (
                (d.official_title || "").toLowerCase().includes(q) ||
                (d.id || "").toLowerCase().includes(q) ||
                (d.qualification || "").toLowerCase().includes(q) ||
                (d.campus || "").toLowerCase().includes(q)
              );
            })
            .map((d) => (
            <li key={d.id} className="p-4 flex items-center justify-between gap-4" data-testid={`uni-portal-degree-row-${d.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-mono text-sm flex items-center gap-2">
                  {d.official_title || d.title || d.name || d.id}
                  {!d.official_title && !d.title && d.name && (
                    <span className="text-[10px] font-underground-sc text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                      overview
                    </span>
                  )}
                </div>
                <div className="text-[10px] font-underground-sc text-stone-400 mt-0.5">
                  {d.qualification || "—"} · {d.campus || "main campus"}
                  {d.content_verified ? " · verified" : " · pending verify"}
                  {d.last_uni_update_at ? ` · edited by uni ${new Date(d.last_uni_update_at).toLocaleDateString()}` : ""}
                </div>
                {typeof d.verified_pct === "number" && (
                  <div
                    className="mt-2 flex items-center gap-2"
                    data-testid={`uni-portal-degree-progress-${d.id}`}
                  >
                    <div className="flex-1 h-1.5 rounded-full bg-stone-200 overflow-hidden max-w-[240px]">
                      <div
                        className="h-full bg-emerald-600 transition-[width] duration-500"
                        style={{ width: `${Math.max(2, Math.min(100, d.verified_pct))}%` }}
                        aria-label={`${d.verified_pct}% of registry fields verified`}
                      />
                    </div>
                    <span className="text-[10px] font-mono text-stone-500 tabular-nums">
                      {d.verified_pct}%
                      {typeof d.verified_count === "number" && typeof d.verified_total === "number" && (
                        <span className="text-stone-400"> · {d.verified_count}/{d.verified_total}</span>
                      )}
                    </span>
                  </div>
                )}
              </div>
              <Link to={`/uni-portal/degree/${d.id}`}>
                <Button size="sm" variant="outline" data-testid={`uni-portal-degree-edit-${d.id}`}>
                  Edit
                </Button>
              </Link>
            </li>
          ))}
          {degrees.length > 0 && degrees.filter((d) => {
            if (!degreeFilter.trim()) return true;
            const q = degreeFilter.trim().toLowerCase();
            return (
              (d.official_title || "").toLowerCase().includes(q) ||
              (d.id || "").toLowerCase().includes(q) ||
              (d.qualification || "").toLowerCase().includes(q) ||
              (d.campus || "").toLowerCase().includes(q)
            );
          }).length === 0 && (
            <li className="p-6 text-sm text-stone-500 text-center italic">
              No degrees match "{degreeFilter}".
            </li>
          )}
          {degrees.length === 0 && (
            <li className="p-6 text-sm text-stone-500 text-center">
              No degrees on file for this university yet.
            </li>
          )}
        </ul>
      </div>
    </div>
  );
}
